import { allRegimens, findRegimen, type ChoiceGroup, type Meal, type MealMenu, type Regimen } from "@/data/meal-plan";
import type { DayPlan, MealSelections } from "@/types/meal-plan-state";
import {
  buildDinnerFromLunch,
  buildMealSelectionsFromNote,
  menuCandidatesForNote,
  normalize,
  pickMenuForNote,
  scanNoteForGroup,
} from "./ingredient-mapping";
import { clearMeals, getMealChoices, getMealMenu, setDayMealMenu } from "./random-plan";
import { isMealEmpty } from "./copy-meal-notes";

/**
 * The compact Comidas screen, as pure functions: one dish per day that fills
 * lunch and dinner ("same"), or a dinner of its own ("distinct"). Whether a
 * day is "same" is read from its notes; nothing new is stored.
 */

export type ComidasMeal = "lunch" | "dinner";
/** Which meals a saved note fills: "both" in a "same" day. */
export type ComidasTarget = ComidasMeal | "both";
export type ComidasMode = "same" | "distinct";

/** Index of `name` in the day's regimen, when the day has that meal and meals to fill. */
export function dayMealIndex(day: DayPlan, name: ComidasMeal): number | null {
  const regimen = findRegimen(day.regimenId);
  if (!regimen || !day.meals) return null;
  const index = regimen.meals.findIndex((m) => m.name === name);
  return index === -1 || !day.meals[index] ? null : index;
}

export interface DayLunchDinner {
  regimen: Regimen;
  lunchIdx: number;
  dinnerIdx: number;
  lunch: Meal;
  dinner: Meal;
}

/** The day's lunch and dinner, or null when its regimen lacks either (or it has none). */
export function dayLunchDinner(day: DayPlan): DayLunchDinner | null {
  const regimen = findRegimen(day.regimenId);
  const lunchIdx = dayMealIndex(day, "lunch");
  const dinnerIdx = dayMealIndex(day, "dinner");
  if (!regimen || lunchIdx === null || dinnerIdx === null) return null;
  return { regimen, lunchIdx, dinnerIdx, lunch: regimen.meals[lunchIdx]!, dinner: regimen.meals[dinnerIdx]! };
}

export function noteAt(day: DayPlan, index: number): string {
  return (day.mealNotes?.[index] ?? "").trim();
}

/** Same dish: equal ignoring case, accents and surrounding spaces. */
export function sameNote(a: string, b: string): boolean {
  return normalize(a.trim()) === normalize(b.trim());
}

/**
 * "same" while dinner's note is lunch's and dinner is still what "same"
 * makes of it (`dinnerLikeLunch`): a dinner changed by hand is its own, so
 * the day shows two inputs and saving lunch leaves that dinner alone. Two
 * empty notes are "same" only while both meals are empty too: foods picked
 * by hand on the day screen have no text to share.
 */
export function comidasMode(day: DayPlan, ld: DayLunchDinner): ComidasMode {
  const lunchNote = noteAt(day, ld.lunchIdx);
  if (!sameNote(lunchNote, noteAt(day, ld.dinnerIdx))) return "distinct";
  if (lunchNote === "") {
    return isMealEmpty(day, ld.lunch, ld.lunchIdx) && isMealEmpty(day, ld.dinner, ld.dinnerIdx) ? "same" : "distinct";
  }
  return mealKey(dinnerLikeLunch(day), ld.dinnerIdx) === mealKey(day, ld.dinnerIdx) ? "same" : "distinct";
}

/** A meal's menu and picks, ignoring "comido" ticks and pick order, for equality. */
function mealKey(day: DayPlan, index: number): string {
  const groups = (day.meals?.[index] ?? []).map((cg) => [
    cg.skipped ? 1 : 0,
    cg.selections
      .filter((s) => s.count > 0)
      .map((s) => `${s.optionId}:${s.count}`)
      .sort(),
  ]);
  return JSON.stringify([day.mealMenus?.[index] ?? null, groups]);
}

/**
 * The menu a Comidas note fills: `undefined` for regular meals, `null` for
 * none. A menu chosen by hand stays. One that came from the previous note
 * (its pick, or one of its candidates chosen in "¿Qué opción es?") moves
 * when the new note picks another one, stays while the new note could
 * still mean it, and goes when it can't: keeping it would show the old
 * dish's foods as if the new one matched.
 */
function comidasMenu(note: string, meal: Meal, chosen: string | undefined, previousNote: string): string | undefined | null {
  if (!meal.menus) return undefined;
  const picked = pickMenuForNote(note, meal);
  if (chosen === undefined) return picked;
  const fromNote =
    previousNote !== "" &&
    (pickMenuForNote(previousNote, meal) === chosen || menuCandidatesForNote(previousNote, meal).includes(chosen));
  if (!fromNote) return chosen;
  if (picked !== null) return picked;
  return menuCandidatesForNote(note, meal).includes(chosen) ? chosen : null;
}

function withNote(day: DayPlan, index: number, note: string): DayPlan {
  const notes = { ...day.mealNotes };
  if (note) notes[index] = note;
  else delete notes[index];
  return { ...day, mealNotes: notes };
}

function withMenu(day: DayPlan, meal: Meal, index: number, menuId: string | undefined): DayPlan {
  return menuId !== undefined && menuId !== day.mealMenus?.[index] ? setDayMealMenu(day, meal, index, menuId) : day;
}

function withSelections(day: DayPlan, index: number, selections: MealSelections): DayPlan {
  return { ...day, meals: day.meals!.map((m, i) => (i === index ? selections : m)) };
}

/**
 * Typing `note` on one meal: the note, plus the menu and foods it names
 * (`comidasMenu`: a menu meal the note can't place keeps only the note).
 */
function fillFromNote(day: DayPlan, meal: Meal, index: number, note: string): DayPlan {
  const chosen = day.mealMenus?.[index];
  const menuId = comidasMenu(note, meal, chosen, noteAt(day, index));
  const next = withNote(day, index, note);
  if (menuId === null) return chosen === undefined ? next : setDayMealMenu(next, meal, index, null);
  return withSelections(withMenu(next, meal, index, menuId), index, buildMealSelectionsFromNote(note, meal, menuId));
}

/**
 * Dinner in a "same" day: lunch's foods when dinner offers them (same dish,
 * dinner portions), else the note read on dinner alone (a lunch the note
 * only half fills still gets the dinner it can). A menu dinner the note
 * can't place follows lunch's menu when it has one of that id. `before` is
 * the day before the save (dinner's previous note decides menu moves).
 */
function fillDinnerLikeLunch(next: DayPlan, before: DayPlan, ld: DayLunchDinner, note: string, forceMenu?: string): DayPlan {
  const lunchMenu = next.mealMenus?.[ld.lunchIdx];
  let dinnerMenu: string | undefined | null =
    forceMenu ?? comidasMenu(note, ld.dinner, before.mealMenus?.[ld.dinnerIdx], noteAt(before, ld.dinnerIdx));
  if (dinnerMenu === null && lunchMenu !== undefined && ld.dinner.menus?.some((m) => m.id === lunchMenu)) {
    dinnerMenu = lunchMenu;
  }
  let day = withNote(next, ld.dinnerIdx, note);
  if (dinnerMenu === null) {
    return day.mealMenus?.[ld.dinnerIdx] === undefined ? day : setDayMealMenu(day, ld.dinner, ld.dinnerIdx, null);
  }
  day = withMenu(day, ld.dinner, ld.dinnerIdx, dinnerMenu);
  const mirror = buildDinnerFromLunch(ld.lunch, next.meals![ld.lunchIdx]!, ld.dinner, {
    lunch: lunchMenu,
    dinner: dinnerMenu,
  });
  return withSelections(
    day,
    ld.dinnerIdx,
    mirror.feasible ? mirror.selections : buildMealSelectionsFromNote(note, ld.dinner, dinnerMenu),
  );
}

/**
 * Save a Comidas note: `target` "both" fills lunch from the note and dinner
 * like lunch (`fillDinnerLikeLunch`), with dinner's note set to the same
 * text; "lunch" / "dinner" fill just that meal. An empty note clears the
 * target meals (foods, menu and note). A meal the day doesn't have is
 * skipped ("both" on a day with only lunch fills lunch). Pure.
 */
export function saveComidasNote(day: DayPlan, target: ComidasTarget, note: string): DayPlan {
  const regimen = findRegimen(day.regimenId);
  const lunchIdx = dayMealIndex(day, "lunch");
  const dinnerIdx = dayMealIndex(day, "dinner");
  if (!regimen) return day;
  const text = note.trim();
  const indices = (target === "both" ? [lunchIdx, dinnerIdx] : [target === "lunch" ? lunchIdx : dinnerIdx]).filter(
    (i): i is number => i !== null,
  );
  if (indices.length === 0) return day;
  if (!text) return clearMeals(day, regimen, indices);

  const ld = dayLunchDinner(day);
  if (target === "both" && ld) {
    return fillDinnerLikeLunch(fillFromNote(day, ld.lunch, ld.lunchIdx, text), day, ld, text);
  }
  let next = day;
  for (const index of indices) next = fillFromNote(next, regimen.meals[index]!, index, text);
  return next;
}

/**
 * "Igual que el almuerzo": dinner takes lunch's note and is filled like it
 * (lunch, hand tweaks included, stays as is). With no lunch note, dinner is
 * cleared.
 */
export function dinnerLikeLunch(day: DayPlan): DayPlan {
  const ld = dayLunchDinner(day);
  if (!ld) return day;
  const note = noteAt(day, ld.lunchIdx);
  return note ? fillDinnerLikeLunch(day, day, ld, note) : saveComidasNote(day, "dinner", "");
}

/**
 * The person picked a menu for a note no menu matched alone ("¿Qué opción
 * es?"): choose it on the target meals that offer it and fill it from the
 * note. In "both", dinner follows lunch inside that menu.
 */
export function chooseComidasMenu(day: DayPlan, target: ComidasTarget, menuId: string): DayPlan {
  const ld = dayLunchDinner(day);
  const regimen = findRegimen(day.regimenId);
  if (!regimen || !day.meals) return day;
  const pick = (index: number) => {
    const meal = regimen.meals[index]!;
    if (!meal.menus?.some((m) => m.id === menuId)) return null;
    return meal;
  };
  const fillIn = (d: DayPlan, index: number) => {
    const meal = pick(index);
    if (!meal) return d;
    const chosen = setDayMealMenu(d, meal, index, menuId);
    return withSelections(chosen, index, buildMealSelectionsFromNote(noteAt(d, index), meal, menuId));
  };
  if (target === "both" && ld) {
    const lunchDone = fillIn(day, ld.lunchIdx);
    if (!pick(ld.dinnerIdx)) return lunchDone;
    return fillDinnerLikeLunch(lunchDone, day, ld, noteAt(day, ld.lunchIdx), menuId);
  }
  const index = dayMealIndex(day, target === "dinner" ? "dinner" : "lunch");
  return index === null ? day : fillIn(day, index);
}

// ── What a note matched ──

export type MatchStatus = "empty" | "ok" | "partial" | "none" | "ambiguous";
export type MainCategory = "carb" | "protein";

export interface MealMatch {
  status: MatchStatus;
  /** "partial": the carb / protein groups left empty. */
  missing: MainCategory[];
  /** Foods the note names that this meal doesn't offer, for the missing categories ("Cerdo"). */
  unknown: string[];
  /** "ambiguous": the menus the note could mean. */
  candidates: MealMenu[];
  /** The chosen menu's name and the carb / protein foods picked, e.g. "Opción 1 · Pollo, Arroz blanco". */
  foods: string;
}

const MAIN: MainCategory[] = ["carb", "protein"];

/** Every carb / protein food of every plan, by category: what "not in your plan" is checked against. */
const KNOWN_FOODS: Record<MainCategory, ChoiceGroup> = (() => {
  const names: Record<MainCategory, Set<string>> = { carb: new Set(), protein: new Set() };
  for (const regimen of allRegimens)
    for (const meal of regimen.meals)
      for (const group of [...meal.choices, ...(meal.menus ?? []).flatMap((m) => m.choices)])
        if (group.category === "carb" || group.category === "protein")
          for (const o of group.options) names[group.category].add(o.name);
  const group = (category: MainCategory): ChoiceGroup => ({
    category,
    pick: 1,
    canRepeat: false,
    options: [...names[category]].map((name) => ({ id: name, name, portion: "" })),
  });
  return { carb: group("carb"), protein: group("protein") };
})();

function offeredNames(meal: Meal): Set<string> {
  const groups = [...meal.choices, ...(meal.menus ?? []).flatMap((m) => m.choices)];
  return new Set(groups.flatMap((g) => g.options.map((o) => o.name)));
}

/** "Opción 1 · Pollo, Arroz blanco": the menu, then the picked proteins and carbs (no counts). */
export function mealFoods(meal: Meal, menuId: string | undefined, sels: MealSelections): string {
  const choices = getMealChoices(meal, menuId);
  const names: string[] = [];
  for (const category of ["protein", "carb"] as const) {
    choices.forEach((group, ci) => {
      if (group.category !== category) return;
      const cg = sels[ci];
      if (!cg || cg.skipped) return;
      for (const s of cg.selections) {
        const name = group.options.find((o) => o.id === s.optionId)?.name;
        if (name && s.count > 0 && !names.includes(name)) names.push(name);
      }
    });
  }
  return [getMealMenu(meal, menuId)?.name, names.join(", ")].filter(Boolean).join(" · ");
}

/**
 * How well a saved note filled its meal: "ok" with a carb and a protein (a
 * skipped or optional group counts as handled), "partial" with one missing,
 * "none" with neither, "ambiguous" for a menu meal whose note several menus
 * could mean (no menu chosen), "empty" without a note.
 */
export function mealMatch(note: string, meal: Meal, menuId: string | undefined, sels: MealSelections): MealMatch {
  const foods = mealFoods(meal, menuId, sels);
  const result: MealMatch = { status: "empty", missing: [], unknown: [], candidates: [], foods };
  if (!note.trim()) return result;

  if (meal.menus && !getMealMenu(meal, menuId)) {
    const ids = menuCandidatesForNote(note, meal);
    result.candidates = meal.menus.filter((m) => ids.includes(m.id));
    result.status = result.candidates.length > 0 ? "ambiguous" : "none";
    if (result.status === "none") result.unknown = unknownFoods(note, meal, MAIN);
    return result;
  }

  const choices = getMealChoices(meal, menuId);
  const picked = (category: MainCategory) =>
    choices.some((g, i) => g.category === category && (sels[i]?.selections.length ?? 0) > 0);
  const handled = (category: MainCategory) =>
    choices.every(
      (g, i) => g.category !== category || g.optional || sels[i]?.skipped || (sels[i]?.selections.length ?? 0) > 0,
    );
  if (!picked("carb") && !picked("protein") && !MAIN.every(handled)) {
    result.status = "none";
    result.unknown = unknownFoods(note, meal, MAIN);
    return result;
  }
  result.missing = MAIN.filter((c) => !handled(c));
  result.status = result.missing.length > 0 ? "partial" : "ok";
  result.unknown = unknownFoods(note, meal, result.missing);
  return result;
}

function unknownFoods(note: string, meal: Meal, categories: MainCategory[]): string[] {
  const offered = offeredNames(meal);
  return categories.flatMap((c) => scanNoteForGroup(note, KNOWN_FOODS[c]).filter((name) => !offered.has(name)));
}
